import React, { useEffect, useState } from "react";
import axios from "../api/axiosInstance";

import {
  Paper,
  Typography,
  Box,
  TextField,
  MenuItem,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Tooltip,
  Breadcrumbs,
  Link,
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";

import { useNavigate } from "react-router-dom";
import { useClientInfo } from "../utils/commonImports";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { drawClientHeader } from "../utils/reportClientHeader";
import { addPdfFooter } from "../utils/reportFooter";
import { printPdfDoc } from "../utils/fy";

const REPORTS_PATH = "/reports"; // change if your dashboard route differs

/* ============================
   COLUMNS
============================ */
const VENDOR_COLUMNS = [
  { key: "vendorname", label: "Vendor Name" },
  { key: "vendortype", label: "Type" },
  { key: "vendorprimarycontactname", label: "Contact Person" },
  { key: "vendorprimarycontactphone", label: "Mobile" },
  { key: "vendorprimarycontactemailaddress", label: "Email" },
  { key: "vendorgstin", label: "GSTIN" },
  { key: "vendorbillingcity", label: "City" },
  { key: "vendorbillingstate", label: "State" },
  { key: "vendoractivestatus", label: "Status" },
  { key: "vendoropeningbalance", label: "Opening Balance" },
];

const formatCell = (key, r) =>
  key === "vendoropeningbalance"
    ? Number(r[key] || 0).toFixed(2)
    : r[key];

export default function VendorReport() {
  const navigate = useNavigate();
  const { clientId, client } = useClientInfo();

  const [rows, setRows] = useState([]);

  const [search, setSearch] = useState("");
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");

  const [orderBy, setOrderBy] = useState("");
  const [order, setOrder] = useState("asc");

  /* ========================
        BACK BUTTON -> Reports dashboard
  ======================== */
  const handleBack = () => navigate(REPORTS_PATH);

  /* ========================
        LOAD REPORT
  ======================== */
  const loadReport = async () => {
    try {
      const res = await axios.get("/api/vendors/report", {
        params: {
          client_id: clientId,
          search,
          type,
          status,
        },
      });

      setRows(res.data || []);
    } catch (err) {
      console.error("Failed to load vendor report:", err);
      setRows([]);
    }
  };

  useEffect(() => {
    if (clientId) loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  /* ========================
        SORT
  ======================== */
  const handleSort = (field) => {
    const isAsc = orderBy === field && order === "asc";
    const newOrder = isAsc ? "desc" : "asc";

    setOrder(newOrder);
    setOrderBy(field);

    const sorted = [...rows].sort((a, b) => {
      if ((a[field] || "") < (b[field] || ""))
        return newOrder === "asc" ? -1 : 1;
      if ((a[field] || "") > (b[field] || ""))
        return newOrder === "asc" ? 1 : -1;
      return 0;
    });

    setRows(sorted);
  };

  /* ========================
        PDF
  ======================== */
  const generateVendorPDF = () => {
    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });

    const headerEndY = drawClientHeader(doc, client);

    doc.setFontSize(13);
    doc.setFont(undefined, "bold");
    doc.text("Vendor Report", 14, headerEndY);

    const columns = VENDOR_COLUMNS.map((c) => c.label);
    const body = rows.map((r) =>
      VENDOR_COLUMNS.map((c) => formatCell(c.key, r)),
    );

    autoTable(doc, {
      head: [columns],
      body,
      startY: headerEndY + 6,
      theme: "grid",
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: {
        fillColor: [230, 230, 230],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },
    });

    // FOOTER (ALWAYS LAST)
    addPdfFooter(doc);

    return doc;
  };

  /* ========================
     PRINT / PDF / EXCEL
  ======================== */
  const handlePrint = () => printPdfDoc(generateVendorPDF());

  const handlePDF = () => {
    generateVendorPDF().save("Vendor_Report.pdf");
  };

  const handleExcel = () => {
    const data = rows.map((r) => {
      const obj = {};
      VENDOR_COLUMNS.forEach((c) => {
        obj[c.label] = formatCell(c.key, r);
      });
      return obj;
    });

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Vendors");
    XLSX.writeFile(wb, "Vendor_Report.xlsx");
  };

  /* ========================
        UI
  ======================== */
  return (
    <Paper className="print-report" sx={{ p: 3 }}>
      {/* HEADER WITH BACK BUTTON */}
      <Box display="flex" alignItems="center" gap={1.5} mb={2}>
        <Tooltip title="Back">
          <IconButton
            onClick={handleBack}
            aria-label="Go back"
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              "&:hover": { background: "#e8f1ff", color: "#1a73e8" },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>

        <Box>
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            aria-label="breadcrumb"
          >
            <Link
              component="button"
              type="button"
              underline="hover"
              color="text.secondary"
              variant="body2"
              onClick={() => navigate(REPORTS_PATH)}
            >
              Reports
            </Link>
            <Typography variant="body2" color="text.primary" fontWeight={600}>
              Vendors
            </Typography>
          </Breadcrumbs>

          <Typography variant="h6" fontWeight={700}>
            Vendor Report
          </Typography>
        </Box>
      </Box>

      {/* FILTERS */}
      <Box display="flex" gap={1.5} mb={1.5} flexWrap="wrap">
        <TextField
          size="small"
          label="Search Name / Mobile / GSTIN"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 260 }}
        />

        <TextField
          size="small"
          select
          label="Vendor Type"
          value={type}
          onChange={(e) => setType(e.target.value)}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="Fuel">Fuel</MenuItem>
          <MenuItem value="Maintenance">Maintenance</MenuItem>
          <MenuItem value="Other">Other</MenuItem>
        </TextField>

        <TextField
          size="small"
          select
          label="Status"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          sx={{ minWidth: 120 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="Active">Active</MenuItem>
          <MenuItem value="Inactive">Inactive</MenuItem>
        </TextField>

        <Button variant="contained" size="small" onClick={loadReport}>
          Search
        </Button>

        <Button variant="outlined" size="small" onClick={handlePrint}>
          Print
        </Button>

        <Button
          variant="outlined"
          size="small"
          color="error"
          onClick={handlePDF}
        >
          PDF
        </Button>

        <Button
          variant="outlined"
          size="small"
          color="success"
          onClick={handleExcel}
        >
          Excel
        </Button>
      </Box>

      {/* TABLE */}
      <Table size="small">
        <TableHead sx={{ background: "#f1f5f9" }}>
          <TableRow>
            {VENDOR_COLUMNS.map((col) => (
              <TableCell
                key={col.key}
                sx={{ fontSize: 11, fontWeight: 700, cursor: "pointer" }}
                onClick={() => handleSort(col.key)}
              >
                {col.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {rows.map((r, idx) => (
            <TableRow key={r.vendorid ?? idx}>
              {VENDOR_COLUMNS.map((col) => (
                <TableCell key={col.key} sx={{ fontSize: 12 }}>
                  {formatCell(col.key, r)}
                </TableCell>
              ))}
            </TableRow>
          ))}

          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={VENDOR_COLUMNS.length} align="center">
                No records found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Paper>
  );
}
